import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

type SidebarContextType = {
  collapsed: boolean;
  toggle: () => void;
  setCollapsed: (next: boolean) => void;
};

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

/** Desktop: rail expanded by default. Narrow viewports: closed drawer until user opens it. */
export function sidebarOpenForViewport(): boolean {
  if (typeof window === 'undefined') return true;
  return !window.matchMedia('(max-width: 768px)').matches;
}

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [collapsed, setCollapsedState] = useState(false);

  const toggle = useCallback(() => {
    setCollapsedState((prev) => !prev);
  }, []);

  const setCollapsed = useCallback((next: boolean) => {
    setCollapsedState(next);
  }, []);

  const value = useMemo(
    () => ({
      collapsed,
      toggle,
      setCollapsed,
    }),
    [collapsed, toggle, setCollapsed]
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
};

export const useSidebar = () => {
  const ctx = useContext(SidebarContext);
  if (!ctx) {
    throw new Error('useSidebar must be used within SidebarProvider');
  }
  return ctx;
};

